import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Wallet, CheckCircle2, XCircle, Loader2, AlertTriangle, Download, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

type Tab = 'awaiting_verification' | 'pending' | 'approved' | 'rejected';

const BANK_REJECT_REASONS = [
  'Reference number not found in bank statement',
  'Amount on slip does not match request',
  'Payment slip is unreadable or missing',
  'Funds credited to wrong account',
  'Suspected duplicate / already credited',
  'Other (explain below)',
];
const MIN_REASON_LEN = 10;

export default function WalletTopUpApprovals() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('awaiting_verification');
  const [rejecting, setRejecting] = useState<{ id: string; method: string } | null>(null);
  const [reasonCategory, setReasonCategory] = useState<string>('');
  const [reason, setReason] = useState('');
  const [providerRef, setProviderRef] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);

  const isBank = rejecting?.method === 'bank_transfer';
  const composedReason = isBank
    ? [reasonCategory, reason.trim()].filter(Boolean).join(reasonCategory && reason.trim() ? ' — ' : '')
    : reason.trim();
  const reasonError = (() => {
    if (isBank) {
      if (!reasonCategory) return 'Select a rejection category.';
      if (reasonCategory.startsWith('Other') && reason.trim().length < MIN_REASON_LEN)
        return `Explanation must be at least ${MIN_REASON_LEN} characters.`;
    } else if (reason.trim().length < MIN_REASON_LEN) {
      return `Reason must be at least ${MIN_REASON_LEN} characters.`;
    }
    return '';
  })();

  const { data: list, isLoading } = useQuery({
    queryKey: ['admin-topup-list', tab],
    queryFn: async () => {
      const statuses = tab === 'awaiting_verification' ? ['pending', 'awaiting_verification'] : [tab];
      const { data } = await supabase
        .from('wallet_topup_requests' as any)
        .select('*').in('status', statuses)
        .order('created_at', { ascending: false }).limit(200);
      const rows = (data as any[]) || [];
      const userIds = [...new Set(rows.map((r) => r.user_id))];
      if (!userIds.length) return rows;
      const { data: profs } = await supabase.from('profiles')
        .select('user_id, full_name').in('user_id', userIds);
      const map = Object.fromEntries((profs || []).map((p: any) => [p.user_id, p.full_name]));
      return rows.map((r) => ({ ...r, _user_name: map[r.user_id] || r.user_id }));
    },
  });

  const approve = async (id: string) => {
    setBusy(id);
    const { error } = await supabase.rpc('approve_wallet_topup' as any, {
      _request_id: id,
      _provider_ref: providerRef[id]?.trim() || null,
    });
    setBusy(null);
    if (error) return toast.error(error.message);
    toast.success('Top-up approved & wallet credited');
    qc.invalidateQueries({ queryKey: ['admin-topup-list'] });
  };

  const closeReject = () => {
    setRejecting(null); setReason(''); setReasonCategory('');
  };

  const reject = async () => {
    if (!rejecting) return;
    if (reasonError) return toast.error(reasonError);
    setBusy(rejecting.id);
    const { error } = await supabase.rpc('reject_wallet_topup' as any, {
      _request_id: rejecting.id, _reason: composedReason,
    });
    setBusy(null);
    if (error) return toast.error(error.message);
    toast.success('Top-up rejected');
    closeReject();
    qc.invalidateQueries({ queryKey: ['admin-topup-list'] });
  };

  return (
    <div className="space-y-6 max-w-6xl">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Wallet className="h-6 w-6 text-primary" /> Wallet Top-Up Approvals
        </h1>
        <p className="text-sm text-muted-foreground">Verify Bank / Telebirr / SantimPay payments and credit user wallets.</p>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
        <TabsList>
          <TabsTrigger value="awaiting_verification">Awaiting Verification</TabsTrigger>
          <TabsTrigger value="approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected">Rejected</TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="space-y-3">
          {isLoading ? (
            <Card><CardContent className="p-6 text-center text-muted-foreground">Loading…</CardContent></Card>
          ) : !list?.length ? (
            <Card><CardContent className="p-6 text-center text-muted-foreground">Nothing here.</CardContent></Card>
          ) : list.map((r: any) => (
            <Card key={r.id}>
              <CardContent className="p-4 grid md:grid-cols-[1fr,auto] gap-4 items-start">
                <div className="space-y-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-lg font-bold">{Number(r.amount_etb).toLocaleString()} ETB</span>
                    <Badge variant="outline" className="capitalize">{r.method.replace('_', ' ')}</Badge>
                    <Badge variant={r.status === 'approved' ? 'default' : r.status === 'rejected' ? 'destructive' : 'secondary'} className="capitalize">
                      {r.status.replace('_', ' ')}
                    </Badge>
                  </div>
                  <p className="text-sm">
                    <span className="font-medium">{r._user_name}</span>
                    <span className="text-xs text-muted-foreground"> · {format(new Date(r.created_at), 'd MMM yyyy, HH:mm')}</span>
                  </p>
                  <div className="text-xs text-muted-foreground space-y-0.5">
                    {r.bank_name && <p>Bank: <span className="font-medium text-foreground">{r.bank_name}</span></p>}
                    {r.payment_reference && <p>Ref: <span className="font-mono text-foreground">{r.payment_reference}</span></p>}
                    {r.contact_phone && <p>Phone: <span className="text-foreground">{r.contact_phone}</span></p>}
                    {r.notes && <p>Notes: <span className="text-foreground">{r.notes}</span></p>}
                    {r.approved_at && (
                      <p className="text-emerald-600">
                        Approved {format(new Date(r.approved_at), 'd MMM yyyy, HH:mm')}
                      </p>
                    )}
                    {r.rejected_at && (
                      <p className="text-destructive">
                        Rejected {format(new Date(r.rejected_at), 'd MMM yyyy, HH:mm')}
                        {r.rejection_reason ? ` — ${r.rejection_reason}` : ''}
                      </p>
                    )}
                  </div>
                  <ReceiptList requestId={r.id} />
                  <TransitionHistory requestId={r.id} />
                </div>

                {(r.status === 'pending' || r.status === 'awaiting_verification') && (
                  <div className="flex flex-col gap-2 w-full md:w-64">
                    <Input
                      placeholder="Optional provider tx ref"
                      value={providerRef[r.id] || ''}
                      onChange={(e) => setProviderRef((p) => ({ ...p, [r.id]: e.target.value }))}
                      className="h-8 text-xs"
                    />
                    <div className="flex gap-2">
                      <Button size="sm" className="flex-1 gap-1" disabled={busy === r.id}
                        onClick={() => approve(r.id)}>
                        {busy === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                        Approve
                      </Button>
                      <Button size="sm" variant="outline" className="flex-1 gap-1"
                        onClick={() => setRejecting({ id: r.id, method: r.method })}>
                        <XCircle className="h-3.5 w-3.5" /> Reject
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </TabsContent>
      </Tabs>

      <Dialog open={!!rejecting} onOpenChange={(o) => !o && closeReject()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject top-up request</DialogTitle>
            {isBank && (
              <DialogDescription className="flex items-start gap-2 text-destructive">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>
                  Bank Transfer rejections require a documented reason for audit. The user
                  will see this message and it is logged against your account.
                </span>
              </DialogDescription>
            )}
          </DialogHeader>

          {isBank && (
            <div className="space-y-2">
              <Label>Rejection category <span className="text-destructive">*</span></Label>
              <Select value={reasonCategory} onValueChange={setReasonCategory}>
                <SelectTrigger><SelectValue placeholder="Select a reason" /></SelectTrigger>
                <SelectContent>
                  {BANK_REJECT_REASONS.map((r) => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label>
              {isBank ? 'Additional details' : 'Reason'}
              {(!isBank || reasonCategory.startsWith('Other')) && (
                <span className="text-destructive"> *</span>
              )}
            </Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value.slice(0, 500))}
              placeholder={
                isBank
                  ? 'Add bank statement reference, date checked, or other context…'
                  : 'Explain why this top-up is being rejected'
              }
              rows={4}
              maxLength={500}
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span className={reasonError ? 'text-destructive' : ''}>
                {reasonError || (isBank ? 'Optional unless category is "Other"' : `Min ${MIN_REASON_LEN} characters`)}
              </span>
              <span>{reason.length}/500</span>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeReject}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={reject}
              disabled={!!reasonError || busy === rejecting?.id}
            >
              {busy === rejecting?.id && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              Reject top-up
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TransitionHistory({ requestId }: { requestId: string }) {
  const { data } = useQuery({
    queryKey: ['topup-transitions', requestId],
    queryFn: async () => {
      const { data } = await supabase
        .from('wallet_topup_transitions' as any)
        .select('*')
        .eq('request_id', requestId)
        .order('created_at', { ascending: true });
      return (data as any[]) || [];
    },
  });
  if (!data?.length) return null;
  return (
    <details className="mt-2 text-xs">
      <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
        History ({data.length})
      </summary>
      <ul className="mt-2 space-y-1 border-l pl-3">
        {data.map((t: any) => (
          <li key={t.id} className="text-muted-foreground">
            <span className="font-medium text-foreground capitalize">
              {(t.from_status || 'new').replace('_', ' ')} → {t.to_status.replace('_', ' ')}
            </span>
            {t.actor_role && <> · <span className="capitalize">{t.actor_role.replace('_', ' ')}</span></>}
            {' · '}{format(new Date(t.created_at), 'd MMM, HH:mm')}
            {t.reason && <div className="text-destructive">"{t.reason}"</div>}
          </li>
        ))}
      </ul>
    </details>
  );
}

function ReceiptList({ requestId }: { requestId: string }) {
  const { data } = useQuery({
    queryKey: ['topup-receipts', requestId],
    queryFn: async () => {
      const { data: rows } = await supabase
        .from('wallet_topup_receipts' as any)
        .select('*').eq('request_id', requestId)
        .order('created_at', { ascending: true });
      const list = (rows as any[]) || [];
      const withUrls = await Promise.all(list.map(async (r) => {
        const { data: signed } = await supabase.storage
          .from('wallet-receipts')
          .createSignedUrl(r.storage_path, 60 * 60);
        return { ...r, _url: signed?.signedUrl ?? null };
      }));
      return withUrls;
    },
  });

  const download = async (path: string, name: string) => {
    const { data, error } = await supabase.storage
      .from('wallet-receipts').download(path);
    if (error || !data) return toast.error(error?.message || 'Download failed');
    const url = URL.createObjectURL(data);
    const a = document.createElement('a');
    a.href = url; a.download = name || 'receipt'; a.click();
    URL.revokeObjectURL(url);
  };

  if (!data?.length) return null;
  return (
    <div className="mt-2 space-y-1.5">
      <p className="text-xs font-medium">Bank receipts ({data.length})</p>
      <ul className="space-y-1.5">
        {data.map((r: any) => {
          const isImage = (r.mime_type || '').startsWith('image/');
          return (
            <li key={r.id} className="flex items-center gap-2 rounded border bg-muted/20 p-2 text-xs">
              {isImage && r._url ? (
                <a href={r._url} target="_blank" rel="noopener noreferrer" className="shrink-0">
                  <img src={r._url} alt={r.file_name || 'receipt'}
                    className="h-12 w-12 rounded object-cover border" />
                </a>
              ) : (
                <div className="h-12 w-12 rounded border bg-background flex items-center justify-center shrink-0">
                  <FileText className="h-5 w-5 text-muted-foreground" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{r.file_name || r.storage_path.split('/').pop()}</p>
                <p className="text-muted-foreground">
                  {r.size_bytes ? `${Math.round(r.size_bytes / 1024)} KB` : ''}
                  {r.ocr_extracted?.reference && (
                    <> · ref <span className="font-mono">{r.ocr_extracted.reference}</span></>
                  )}
                  {r.ocr_extracted?.amount ? <> · {Number(r.ocr_extracted.amount).toLocaleString()} ETB</> : null}
                </p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {r._url && (
                  <a href={r._url} target="_blank" rel="noopener noreferrer"
                    className="text-primary hover:underline">View</a>
                )}
                <Button size="icon" variant="ghost" className="h-7 w-7"
                  onClick={() => download(r.storage_path, r.file_name)} title="Download">
                  <Download className="h-3.5 w-3.5" />
                </Button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
